import React from 'react';
import {
    Numbers,
    InfoText,
    AutoTable,
    Dropdown,
    DropdownSort,
    DropdownItem,
    Status,
    CopyToClipboardLink,
    DropdownLine,
    Icon,
    GraphLine,
    Tooltip,
    StopPropagation,
    ToastContext,
    Toggle
} from '@weezevent/nacre';
import { Icon as IconSemantic } from 'semantic-ui-react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import moment from 'moment-timezone';
import Style from './discountTable.module.css';

import { useHistory } from 'react-router-dom';
import { DuplicateAction } from './duplicateAction';
import DiscountCodeModel from '../../../models/discountCodeModel';
import { useSegment } from '../../../components/tracking/trackingProvider';

function DiscountType({ discount, event }) {
    const { discount_rules: rules, discount_type } = discount;
    const locale = i18n.language;
    const { t } = useTranslation();
    console.warn(rules);
    if (discount_type !== 'grid') {
        return (
            <InfoText subtitle={discount_type === 'amount' ? t('ticket.transfers.amount') : t('ticket.common.percentage')}>
                {Number(discount_type === 'amount') ? (
                    <Numbers value={rules[0].amount || 0} locale={locale} currency={event?.currency} />
                ) : (
                    <span className="">{(rules.length ? rules[0].percentage || 0 : 0) + '%'}</span>
                )}
            </InfoText>
        );
    } else {
        return <InfoText subtitle={t('ticket.discountList.table.content.pricing_table')}>{t('ticket.discountList.table.content.discount_grid')}</InfoText>;
    }
}

function ToggleActivation({ discount_code, organization, event }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    let { enabled = false, status = 'unavailable' } = discount_code || {};

    const isEditable = React.useMemo(() => {
        if (status !== 'active' && status !== 'inactive') {
            return false;
        }
        return true;
    }, [status]);

    const _onClick = React.useCallback(() => {
        setIsSubmitting(true);
        discount_code.enabled = !discount_code.enabled;
        const { id, enabled } = discount_code;
        new DiscountCodeModel({ id: [id], enabled })
            .save({
                organizationId: organization.id,
                eventId: event.pk,
                isCreateOrUpdate: true
            })
            .then(() => {
                discount_code.status = discount_code.status === 'active' ? 'inactive' : 'active';
                toast.success(enabled ? t('ticket.discountList.enable.success') : t('ticket.discountList.disabled.success'));
            })
            .catch(e => {
                console.error(e);
                toast.error(enabled ? t('ticket.discountList.enable.error') : t('ticket.discountList.disabled.error'));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [discount_code, organization, event, toast, t]);

    return (
        <div onClick={e => e.stopPropagation()}>
            <Toggle onChange={_onClick} checked={enabled} disabled={!isEditable || isSubmitting} />
        </div>
    );
}

ToggleActivation.propTypes = {
    discount_code: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired
};

function StatusDiscounts({ discount_code }) {
    let { start_datetime = null, status = 'unavailable' } = discount_code || {};
    const { t } = useTranslation();
    const STATUS_COMPONENTS = {
        active: (
            <>
                <Status className="" color="green" icon={null} responsive={false} text={t('ticket.common.active')} />
                {moment(start_datetime).isAfter(new Date()) ? <Status text={<Icon name="time" />} color="purple" /> : null}
            </>
        ),
        inactive: (
            <>
                <Status className="" color="orange" icon={null} responsive={false} text={t('ticket.common.inactive')} />
                {moment(start_datetime).isAfter(new Date()) ? <Status text={<Icon name="time" />} color="purple" /> : null}
            </>
        ),
        removed: <Status className="" color="red" icon={null} responsive={false} text={t('ticket.common.deleted')} />,
        expired: <Status className="" color="" icon={null} responsive={false} text={t('ticket.common.over')} />,
        unavailable: <Status className="" color="" icon={null} responsive={false} text={t('ticket.common.unavailable')} />,
        scheduled: <Status className="" color="purple" icon={null} responsive={false} text={t('ticket.common.scheduled')} />
    };

    return STATUS_COMPONENTS[status];
}
StatusDiscounts.propTypes = {
    discount_code: PropTypes.object.isRequired
};

/**
 * Component to display table of the discounts
 * @param {object} organization: organisation object to have id orga
 * @param {event} event: event object to have id event
 * @param {Array} discounts: Array of discount
 */
export default function DiscountTable({
    organization,
    event,
    discounts,
    pageSize,
    currentPage,
    totalCount,
    onSelected,
    onSelectAll,
    setDiscountsToDelete,
    onPageChange,
    setOrdering,
    ordering,
    sync
}) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const history = useHistory();
    const locale = i18n.language;
    const [selectedDiscounts, setSelectedDiscounts] = React.useState([]);
    const colums = [
        {
            key: 'date_creation',
            label: (
                <DropdownSort
                    name="created"
                    active={['created', '-created'].includes(ordering)}
                    ordering={ordering}
                    items={[
                        <DropdownItem.Icons key="1" item={t('ticket.common.ordering.ascending')} checked={ordering === 'created'} onClick={() => onChangeSort('created')} />,
                        <DropdownItem.Icons key="2" item={t('ticket.common.ordering.descending')} checked={ordering === '-created'} onClick={() => onChangeSort('-created')} />
                    ]}
                    label={t('ticket.discountList.table.headers.dateCreation')}
                    size="mini"
                />
            ),
            render: object => (
                <InfoText subtitle={new Intl.DateTimeFormat(event.default_language, { timeStyle: 'short' }).format(moment(object.created).tz(event.timezone, true))}>
                    {new Intl.DateTimeFormat(event.default_language, { dateStyle: 'short' }).format(moment(object.created).tz(event.timezone, true))}
                </InfoText>
            )
        },
        {
            key: 'code',
            label: (
                <DropdownSort
                    name="code"
                    active={['code', '-code'].includes(ordering)}
                    ordering={ordering}
                    items={[
                        <DropdownItem.Icons key="1" item={t('ticket.common.ordering.ascending')} checked={ordering === 'code'} onClick={() => onChangeSort('code')} />,
                        <DropdownItem.Icons key="2" item={t('ticket.common.ordering.descending')} checked={ordering === '-code'} onClick={() => onChangeSort('-code')} />
                    ]}
                    label={t('ticket.discountList.table.headers.code')}
                    size="mini"
                />
            ),
            render: object => (
                <InfoText subtitle={object.discount?.name}>
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                        {object.code}
                        <StopPropagation>
                            <span
                                className={Style['copy_to_promo_code']}
                                onClick={() => {
                                    track('ACTION > COPY DISCOUNT CODE', { 'Page Title': 'DISCOUNTS', Application: 'WeezTicket nBO' });
                                }}
                            >
                                <CopyToClipboardLink delay={1000} tooltip={null} tooltipCopied={null} value={object.code} disabled={object.status === 'removed'} />
                            </span>
                        </StopPropagation>
                        {object.discount?.is_account_wide && (
                            <Tooltip triggerOnHover={true} title={t('ticket.discountList.table.is_account_wide_tooltip', { event: object.event })}>
                                <IconSemantic name="linkify" className={Style['icon-linkify']} />
                            </Tooltip>
                        )}
                    </span>
                </InfoText>
            )
        },
        {
            key: 'value',
            label: t('ticket.discountList.table.headers.discount'),

            render: object => <DiscountType discount={object.discount} event={event} />
        },
        {
            key: 'quota',
            label: t('ticket.discountList.table.headers.quota'),
            render: object => (
                <InfoText>
                    <GraphLine max={object.quota ? object.quota : '-'} tooltip={false} value={object.usage} locale={locale} />
                </InfoText>
            )
        },
        {
            key: 'validity',
            label: t('ticket.discountList.table.headers.validity'),
            render: object => <ToggleActivation discount_code={object} organization={organization} event={event} sync={sync} />
        },
        {
            key: 'status',
            label: t('ticket.discountList.table.headers.status'),
            render: object => <StatusDiscounts discount_code={object} />
        },
        {
            key: 'actions',
            label: t('ticket.common.cta.actions'),
            render: object => (
                <Dropdown closeOnDocumentClick isLoading={false} offset={[0, 4]} show={false} size="medium" renderWhenClosed disabled={object.status === 'removed'}>
                    <DropdownItem
                        item={t('ticket.common.action.edit')}
                        onClick={() => {
                            track('ACTION > MODIFY DISCOUNT', { 'Page Title': 'DISCOUNTS', Application: 'WeezTicket nBO' });
                            history.push(`/ticket/O${organization.id}/E${event.id}/communicate/discounts/${object.id}/edit`);
                        }}
                    />

                    <DuplicateAction organization={organization} event={event} discount_code={object} sync={sync} />
                    <DropdownLine className="" />
                    <DropdownItem
                        deleteItem
                        item={t('ticket.common.action.delete')}
                        onClick={e => {
                            track('ACTION > DELETE DISCOUNT', { 'Page Title': 'DISCOUNTS', Application: 'WeezTicket nBO', 'Action type': 'Unique' });
                            e.stopPropagation();
                            setDiscountsToDelete([new DiscountCodeModel(object)]);
                        }}
                    />
                </Dropdown>
            )
        }
    ];

    const onChangeSort = ordering => {
        onPageChange(0);
        setOrdering(ordering);
    };

    return (
        <AutoTable
            pk="id"
            useNew
            className={Style['list__table']}
            columns={colums}
            objects={[...discounts]}
            onSelectAll={event => {
                onSelectAll(event);
                if (event.target.checked) {
                    return setSelectedDiscounts(discounts.map(o => o.id));
                }
                setSelectedDiscounts([]);
            }}
            onObjectSelected={(obj, event, index) => {
                onSelected(event, obj);
                let newSelected = Array.from(selectedDiscounts);
                if (event.target.checked) {
                    return setSelectedDiscounts([...newSelected, obj.id]);
                }
                let selected = [...newSelected];
                selected = selected.filter(el => el !== obj.id);
                setSelectedDiscounts(selected);
            }}
            onObjectClicked={(obj, event$, index) => {
                history.push(`/ticket/O${organization.id}/E${event.id}/communicate/discounts/${obj.id}/edit`);
            }}
            pageSize={+pageSize}
            onPageChange={nb => onPageChange(nb)}
            totalCount={+totalCount}
            currentPage={+currentPage}
            paginator
            selectable
            selectedObjects={selectedDiscounts}
        />
    );
}

DiscountTable.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    discounts: PropTypes.array.isRequired,
    pageSize: PropTypes.number.isRequired,
    currentPage: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
    onSelected: PropTypes.func.isRequired,
    onSelectAll: PropTypes.func.isRequired,
    setDiscountsToDelete: PropTypes.func.isRequired,
    onPageChange: PropTypes.func.isRequired,
    setOrdering: PropTypes.func.isRequired,
    ordering: PropTypes.string.isRequired,
    sync: PropTypes.func.isRequired
};
